/// WatchPanel: Pinned variable watch panel with live values.
/// Shows pinned variable paths with type-colored resolved values
/// from the debug session.
///
/// Phase 20, Plan 03.

import { useState, useCallback } from "react";
import { Eye, X, Plus } from "lucide-react";
import { useDebugStore } from "@/stores/debugStore";
import { useVariableWatch } from "@/hooks/useVariableWatch";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WatchPanel() {
  const { watches, pinWatch, unpinWatch } = useVariableWatch();
  const mode = useDebugStore((s) => s.mode);

  const [inputValue, setInputValue] = useState("");

  const handlePin = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    pinWatch(trimmed);
    setInputValue("");
  }, [inputValue, pinWatch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handlePin();
      }
    },
    [handlePin],
  );

  if (mode === "idle") {
    return (
      <div className="flex flex-col items-center justify-center h-full text-zinc-500 gap-2">
        <Eye className="h-6 w-6 text-zinc-600" />
        <span className="text-xs">Start a debug session to watch variables</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Add watch input */}
      <div className="flex items-center gap-1.5 border-b border-zinc-700 px-2 py-1.5 shrink-0">
        <input
          type="text"
          placeholder="Variable path (e.g., amount, customer.name)"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-zinc-900 text-xs text-zinc-300 rounded px-2 py-1 border border-zinc-600 placeholder-zinc-500 focus:outline-none focus:border-blue-500"
        />
        <button
          type="button"
          onClick={handlePin}
          disabled={!inputValue.trim()}
          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors bg-blue-600 text-white hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed"
        >
          <Plus className="h-3 w-3" />
          Pin
        </button>
      </div>

      {/* Watch list */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {watches.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-zinc-600 gap-1 py-4">
            <Eye className="h-5 w-5" />
            <span className="text-xs">No variables pinned. Add a variable path to watch.</span>
          </div>
        ) : (
          watches.map((watch) => (
            <div
              key={watch.variablePath}
              className="flex items-center gap-2 px-3 py-1.5 border-b border-zinc-800 hover:bg-zinc-800/50 group"
            >
              {/* Variable path */}
              <span className="text-xs text-zinc-300 font-mono truncate min-w-[80px]">
                {watch.variablePath}
              </span>

              {/* Separator */}
              <span className="text-zinc-600 text-xs">=</span>

              {/* Value with type coloring */}
              <span
                className={`text-xs font-mono truncate flex-1 ${getValueColor(watch.lastValue)}`}
              >
                {formatWatchValue(watch.lastValue)}
              </span>

              {/* Unpin button */}
              <button
                type="button"
                onClick={() => unpinWatch(watch.variablePath)}
                className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-zinc-300 transition-opacity shrink-0"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get Tailwind color class based on variable value type. */
function getValueColor(value: unknown): string {
  if (value === undefined) return "text-zinc-600";
  if (value === null) return "text-zinc-500";
  if (typeof value === "string") return "text-green-400";
  if (typeof value === "number") return "text-blue-400";
  if (typeof value === "boolean") return "text-red-400";
  return "text-zinc-400"; // object/array
}

/** Format a watch value for display. */
function formatWatchValue(value: unknown): string {
  if (value === undefined) return "undefined";
  if (value === null) return "null";
  if (typeof value === "string")
    return `"${value.length > 30 ? value.slice(0, 30) + "..." : value}"`;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (typeof value === "object") return `Object(${Object.keys(value).length})`;
  return String(value);
}
